import { useState } from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import LanguageSelector from "@/components/LanguageSelector";
import { 
  LayoutDashboard, Globe, FileText, Image, Settings, 
  LogOut, Menu, X, User, Newspaper, CreditCard, Crown
} from "lucide-react";
import { cn } from "@/lib/utils";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_f50ba61d-1f02-4136-b6b8-0e3c71c5b623/artifacts/e4hujy2b_sologo-aitools-bgremover-trimmed.png";

export default function Layout() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const navItems = [
    { path: "/dashboard", icon: LayoutDashboard, label: t('nav.dashboard') },
    { path: "/sites", icon: Globe, label: t('nav.sites') },
    { path: "/articles", icon: FileText, label: t('nav.myArticles') },
    { path: "/wp-posts", icon: Newspaper, label: t('nav.wpArticles') },
    { path: "/media", icon: Image, label: t('nav.media') },
    { path: "/settings", icon: Settings, label: t('nav.settings') },
  ];

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const getInitials = (name) => {
    if (!name) return "U";
    return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  };

  // Get user plan (default to 'solo' if not set)
  const userPlan = user?.plan || 'solo';
  const planNames = {
    solo: 'Solo',
    creator: 'Creator',
    pro: 'Pro',
    business: 'Business',
    agency: 'Agency',
    infinity: 'Infinity'
  };

  return (
    <div className="min-h-screen bg-[#F5F7FA]" data-testid="app-layout">
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 z-40 bg-black/20 backdrop-blur-sm lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed top-0 left-0 z-50 h-screen w-64 bg-white border-r border-gray-100 transition-transform duration-300 lg:translate-x-0 soft-shadow",
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        )}
        data-testid="sidebar"
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-6 border-b border-gray-100">
          <Link to="/dashboard" className="flex items-center gap-2">
            <img src={LOGO_URL} alt="Diffuzo" className="h-7" />
          </Link>
          <Button 
            variant="ghost" 
            size="icon" 
            className="lg:hidden"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-1">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path || 
              (item.path !== "/" && location.pathname.startsWith(item.path));
            
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={cn(
                  "flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200",
                  isActive 
                    ? "bg-[#1E2A5A] text-white" 
                    : "text-[#6B7280] hover:bg-[#F5F7FA] hover:text-[#1E2A5A]"
                )}
                data-testid={`nav-${item.path.replace("/", "")}`}
              >
                <item.icon className="w-5 h-5" strokeWidth={1.5} />
                <span className="font-medium">{item.label}</span>
              </Link>
            );
          })}
        </nav>

        {/* Plan Badge */}
        <div className="absolute bottom-24 left-0 right-0 px-4">
          <Link 
            to="/settings?tab=subscription"
            className="block p-4 bg-gradient-to-r from-[#1E2A5A] to-[#20C997] rounded-xl text-white hover:opacity-90 transition-opacity"
          >
            <div className="flex items-center gap-2 mb-1">
              <Crown className="w-4 h-4" />
              <span className="font-medium text-sm">{planNames[userPlan]}</span>
            </div>
            <p className="text-xs text-white/70">{t('settings.subscription.manage')}</p>
          </Link>
        </div>

        {/* User section at bottom */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-100 bg-white">
          <div className="flex items-center gap-3 px-2">
            <Avatar className="h-10 w-10 border-2 border-[#20C997]">
              <AvatarFallback className="bg-[#1E2A5A] text-white text-sm font-medium">
                {getInitials(user?.name)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-[#1E2A5A] truncate">{user?.name}</p>
              <p className="text-xs text-[#6B7280] truncate">{user?.email}</p>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="lg:pl-64">
        {/* Top Header */}
        <header className="h-16 bg-white/80 backdrop-blur-lg border-b border-gray-100 sticky top-0 z-30">
          <div className="h-full flex items-center justify-between px-4 lg:px-8">
            <Button 
              variant="ghost" 
              size="icon" 
              className="lg:hidden text-[#1E2A5A]"
              onClick={() => setSidebarOpen(true)}
              data-testid="mobile-menu-btn"
            >
              <Menu className="w-5 h-5" />
            </Button>

            <div className="flex-1 lg:hidden flex justify-center">
              <img src={LOGO_URL} alt="Diffuzo" className="h-6" />
            </div>

            <div className="hidden lg:block flex-1" />

            <div className="flex items-center gap-3">
              <LanguageSelector variant="minimal" />
              
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2 hover:bg-[#F5F7FA]" data-testid="user-menu-btn">
                    <Avatar className="h-8 w-8 border-2 border-[#20C997]">
                      <AvatarFallback className="bg-[#1E2A5A] text-white text-sm">
                        {getInitials(user?.name)}
                      </AvatarFallback>
                    </Avatar>
                    <span className="hidden sm:inline font-medium text-[#1E2A5A]">{user?.name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 rounded-xl">
                  <div className="px-3 py-2">
                    <p className="text-sm font-medium text-[#1E2A5A]">{user?.name}</p>
                    <p className="text-xs text-[#6B7280]">{user?.email}</p>
                    <Badge className="mt-1 bg-[#20C997]/10 text-[#20C997] hover:bg-[#20C997]/10 text-xs">
                      {planNames[userPlan]}
                    </Badge>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/settings" className="flex items-center cursor-pointer">
                      <User className="w-4 h-4 mr-2" />
                      {t('settings.profile.title')}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/settings?tab=subscription" className="flex items-center cursor-pointer">
                      <CreditCard className="w-4 h-4 mr-2" />
                      {t('settings.subscription.title')}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/settings" className="flex items-center cursor-pointer">
                      <Settings className="w-4 h-4 mr-2" />
                      {t('nav.settings')}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={handleLogout}
                    className="text-red-500 focus:text-red-500 cursor-pointer"
                    data-testid="logout-btn"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    {t('nav.logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
